export type NuxSurface = 'landing' | 'workspace'

export type NuxPlacement = 'top' | 'right' | 'bottom' | 'left'

export type NuxCondition =
  | { type: 'anchorVisible' }
  | { type: 'nodeVisible'; nodeType?: string }
  | { type: 'nodeReady'; nodeType?: string }
  | { type: 'nodeSelected'; nodeType?: string }
  | { type: 'panelOpen'; panelId?: string; nodeType?: string }

export type NuxCompletion =
  | { type: 'nodeSelected'; nodeType?: string; scope?: 'activeNode' | 'anyNode' }
  | { type: 'panelOpened'; panelId?: string; nodeType?: string }
  | { type: 'editorOpened'; editorId?: string }
  | { type: 'exportCompleted'; editorId?: string }
  | { type: 'voiceAdded'; nodeType?: string }
  | { type: 'controlActivated'; controlId?: string; anchorId?: string; nodeType?: string }

export interface NuxTooltipCopy {
  title: string
  body: string
  skip: string
}

export interface NuxTooltipCopyKeys {
  titleKey: string
  bodyKey: string
  skipKey: string
}

export const NUX_TOOLTIP_COPY_KEYS = {
  landing_setup_choices: {
    titleKey: 'nuxTooltips.landing_setup_choices.title',
    bodyKey: 'nuxTooltips.landing_setup_choices.body',
    skipKey: 'nuxTooltips.landing_setup_choices.skip',
  },
  workspace_director_input: {
    titleKey: 'nuxTooltips.workspace_director_input.title',
    bodyKey: 'nuxTooltips.workspace_director_input.body',
    skipKey: 'nuxTooltips.workspace_director_input.skip',
  },
  workspace_canvas_surface: {
    titleKey: 'nuxTooltips.workspace_canvas_surface.title',
    bodyKey: 'nuxTooltips.workspace_canvas_surface.body',
    skipKey: 'nuxTooltips.workspace_canvas_surface.skip',
  },
  workspace_multi_canvas: {
    titleKey: 'nuxTooltips.workspace_multi_canvas.title',
    bodyKey: 'nuxTooltips.workspace_multi_canvas.body',
    skipKey: 'nuxTooltips.workspace_multi_canvas.skip',
  },
  workspace_click_ready_node: {
    titleKey: 'nuxTooltips.workspace_click_ready_node.title',
    bodyKey: 'nuxTooltips.workspace_click_ready_node.body',
    skipKey: 'nuxTooltips.workspace_click_ready_node.skip',
  },
  scriptwriter_config_refine: {
    titleKey: 'nuxTooltips.scriptwriter_config_refine.title',
    bodyKey: 'nuxTooltips.scriptwriter_config_refine.body',
    skipKey: 'nuxTooltips.scriptwriter_config_refine.skip',
  },
  scriptwriter_add_to_chat: {
    titleKey: 'nuxTooltips.scriptwriter_add_to_chat.title',
    bodyKey: 'nuxTooltips.scriptwriter_add_to_chat.body',
    skipKey: 'nuxTooltips.scriptwriter_add_to_chat.skip',
  },
  character_visual_tools: {
    titleKey: 'nuxTooltips.character_visual_tools.title',
    bodyKey: 'nuxTooltips.character_visual_tools.body',
    skipKey: 'nuxTooltips.character_visual_tools.skip',
  },
  character_add_voice: {
    titleKey: 'nuxTooltips.character_add_voice.title',
    bodyKey: 'nuxTooltips.character_add_voice.body',
    skipKey: 'nuxTooltips.character_add_voice.skip',
  },
  object_add_props: {
    titleKey: 'nuxTooltips.object_add_props.title',
    bodyKey: 'nuxTooltips.object_add_props.body',
    skipKey: 'nuxTooltips.object_add_props.skip',
  },
  shot_list_overview: {
    titleKey: 'nuxTooltips.shot_list_overview.title',
    bodyKey: 'nuxTooltips.shot_list_overview.body',
    skipKey: 'nuxTooltips.shot_list_overview.skip',
  },
  video_generator_modes: {
    titleKey: 'nuxTooltips.video_generator_modes.title',
    bodyKey: 'nuxTooltips.video_generator_modes.body',
    skipKey: 'nuxTooltips.video_generator_modes.skip',
  },
  video_generator_references: {
    titleKey: 'nuxTooltips.video_generator_references.title',
    bodyKey: 'nuxTooltips.video_generator_references.body',
    skipKey: 'nuxTooltips.video_generator_references.skip',
  },
  video_generator_model_selection: {
    titleKey: 'nuxTooltips.video_generator_model_selection.title',
    bodyKey: 'nuxTooltips.video_generator_model_selection.body',
    skipKey: 'nuxTooltips.video_generator_model_selection.skip',
  },
  sound_node_purpose: {
    titleKey: 'nuxTooltips.sound_node_purpose.title',
    bodyKey: 'nuxTooltips.sound_node_purpose.body',
    skipKey: 'nuxTooltips.sound_node_purpose.skip',
  },
  video_editor_actions: {
    titleKey: 'nuxTooltips.video_editor_actions.title',
    bodyKey: 'nuxTooltips.video_editor_actions.body',
    skipKey: 'nuxTooltips.video_editor_actions.skip',
  },
} satisfies Record<string, NuxTooltipCopyKeys>

export type NuxTooltipCopyKey = keyof typeof NUX_TOOLTIP_COPY_KEYS

export interface NuxTooltipDefinition {
  id: string
  surface: NuxSurface
  anchorId: string
  placement: NuxPlacement
  copyKey: NuxTooltipCopyKey
  showWhen: NuxCondition[]
  completeWhen?: NuxCompletion[]
  anchorStrategy?: 'first' | 'union'
}

export const nuxTooltipCatalog: readonly NuxTooltipDefinition[] = [
  {
    id: 'landing_setup_choices',
    surface: 'landing',
    anchorId: 'landing-setup-choices',
    placement: 'bottom',
    copyKey: 'landing_setup_choices',
    showWhen: [{ type: 'anchorVisible' }],
    completeWhen: [
      { type: 'controlActivated', controlId: 'styleOpened' },
      { type: 'controlActivated', controlId: 'skillsOpened' },
      { type: 'controlActivated', controlId: 'projectSetupOpened' },
    ],
  },
  {
    id: 'workspace_director_input',
    surface: 'workspace',
    anchorId: 'director-input',
    placement: 'right',
    copyKey: 'workspace_director_input',
    showWhen: [{ type: 'anchorVisible' }],
    completeWhen: [{ type: 'controlActivated', controlId: 'directorMessageSent' }],
  },
  {
    id: 'workspace_multi_canvas',
    surface: 'workspace',
    anchorId: 'canvas-switcher',
    placement: 'bottom',
    copyKey: 'workspace_multi_canvas',
    showWhen: [{ type: 'anchorVisible' }],
    completeWhen: [
      { type: 'controlActivated', controlId: 'canvasSwitcherOpened' },
      { type: 'controlActivated', controlId: 'canvasCreated' },
      { type: 'controlActivated', controlId: 'canvasImported' },
      { type: 'controlActivated', controlId: 'canvasChanged' },
    ],
  },
  {
    id: 'workspace_click_ready_node',
    surface: 'workspace',
    anchorId: 'node-visible',
    placement: 'right',
    copyKey: 'workspace_click_ready_node',
    showWhen: [{ type: 'nodeReady' }],
    completeWhen: [{ type: 'nodeSelected', scope: 'anyNode' }],
  },
  {
    id: 'scriptwriter_config_refine',
    surface: 'workspace',
    anchorId: 'node-config-refine-tabs',
    placement: 'top',
    copyKey: 'scriptwriter_config_refine',
    showWhen: [
      { type: 'nodeSelected', nodeType: 'scriptWriter' },
      { type: 'panelOpen', nodeType: 'scriptWriter' },
    ],
    completeWhen: [
      { type: 'panelOpened', panelId: 'config', nodeType: 'scriptWriter' },
      { type: 'panelOpened', panelId: 'refine', nodeType: 'scriptWriter' },
    ],
    anchorStrategy: 'union',
  },
  {
    id: 'scriptwriter_add_to_chat',
    surface: 'workspace',
    anchorId: 'node-toolbar-add-to-chat',
    placement: 'top',
    copyKey: 'scriptwriter_add_to_chat',
    showWhen: [{ type: 'nodeSelected', nodeType: 'scriptWriter' }, { type: 'anchorVisible' }],
    completeWhen: [{ type: 'controlActivated', controlId: 'addToChat', nodeType: 'scriptWriter' }],
  },
  {
    id: 'character_visual_tools',
    surface: 'workspace',
    anchorId: 'character-visual-tools',
    placement: 'top',
    copyKey: 'character_visual_tools',
    showWhen: [{ type: 'nodeSelected', nodeType: 'characterDesigner' }, { type: 'anchorVisible' }],
    completeWhen: [{ type: 'controlActivated', controlId: 'annotate', nodeType: 'characterDesigner' }],
    anchorStrategy: 'union',
  },
  {
    id: 'character_add_voice',
    surface: 'workspace',
    anchorId: 'character-add-voice',
    placement: 'bottom',
    copyKey: 'character_add_voice',
    showWhen: [{ type: 'anchorVisible' }],
    completeWhen: [
      { type: 'controlActivated', controlId: 'characterVoiceSourceMenuOpened', anchorId: 'character-add-voice', nodeType: 'characterDesigner' },
      { type: 'voiceAdded', nodeType: 'characterDesigner' },
    ],
  },
  {
    id: 'object_add_props',
    surface: 'workspace',
    anchorId: 'object-add-props',
    placement: 'top',
    copyKey: 'object_add_props',
    showWhen: [{ type: 'nodeSelected', nodeType: 'objectDesigner' }, { type: 'anchorVisible' }],
    completeWhen: [{ type: 'controlActivated', controlId: 'addProps', nodeType: 'objectDesigner' }],
  },
  {
    id: 'shot_list_overview',
    surface: 'workspace',
    anchorId: 'shot-list-overview',
    placement: 'right',
    copyKey: 'shot_list_overview',
    showWhen: [{ type: 'nodeReady', nodeType: 'storyboardDesigner' }, { type: 'anchorVisible' }],
    completeWhen: [{ type: 'controlActivated', controlId: 'shotListInteracted' }],
  },
  {
    id: 'video_generator_modes',
    surface: 'workspace',
    anchorId: 'video-generator-modes',
    placement: 'top',
    copyKey: 'video_generator_modes',
    showWhen: [{ type: 'nodeSelected', nodeType: 'videoGenerator' }, { type: 'anchorVisible' }],
    completeWhen: [
      { type: 'controlActivated', controlId: 'structuredModeSelected', nodeType: 'videoGenerator' },
      { type: 'controlActivated', controlId: 'freeformModeSelected', nodeType: 'videoGenerator' },
    ],
  },
  {
    id: 'video_generator_references',
    surface: 'workspace',
    anchorId: 'video-generator-references',
    placement: 'left',
    copyKey: 'video_generator_references',
    showWhen: [{ type: 'nodeSelected', nodeType: 'videoGenerator' }, { type: 'anchorVisible' }],
    completeWhen: [{ type: 'controlActivated', controlId: 'videoReferencesChanged', nodeType: 'videoGenerator' }],
  },
  {
    id: 'video_generator_model_selection',
    surface: 'workspace',
    anchorId: 'video-generator-model-selection',
    placement: 'left',
    copyKey: 'video_generator_model_selection',
    showWhen: [{ type: 'nodeSelected', nodeType: 'videoGenerator' }, { type: 'anchorVisible' }],
    completeWhen: [{ type: 'controlActivated', controlId: 'videoModelChanged', nodeType: 'videoGenerator' }],
  },
  {
    id: 'sound_node_purpose',
    surface: 'workspace',
    anchorId: 'sound-node-purpose',
    placement: 'top',
    copyKey: 'sound_node_purpose',
    showWhen: [{ type: 'nodeSelected', nodeType: 'soundExpert' }, { type: 'anchorVisible' }],
    completeWhen: [
      { type: 'controlActivated', controlId: 'soundModeSpeech', nodeType: 'soundExpert' },
      { type: 'controlActivated', controlId: 'soundModeMusic', nodeType: 'soundExpert' },
      { type: 'controlActivated', controlId: 'soundModeSfx', nodeType: 'soundExpert' },
    ],
  },
  {
    id: 'video_editor_actions',
    surface: 'workspace',
    anchorId: 'video-editor-controls',
    placement: 'bottom',
    copyKey: 'video_editor_actions',
    showWhen: [{ type: 'nodeReady', nodeType: 'videoEditor' }, { type: 'anchorVisible' }],
    completeWhen: [
      { type: 'editorOpened', editorId: 'timeline' },
      { type: 'exportCompleted', editorId: 'timeline' },
    ],
  },
]
